import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CurriculumState, CurriculumUnit } from '@/models';
import CurriculumService from '@/services/Curriculum';
import { CurriculumUnits } from '@/services/__mocks__/CurriculumUnits';

const initialState: CurriculumState = {
  units: CurriculumUnits,
  loading: true,
};

// Async Thunks
export const addUnit = createAsyncThunk(
  'curriculum/addUnit',
  async (unit: CurriculumUnit) => {
    const response = await CurriculumService.create(unit);
    return response;
  }
);

export const editUnit = createAsyncThunk(
  'curriculum/editUnit',
  async (unit: CurriculumUnit) => {
    const response = await CurriculumService.update(unit);
    return response;
  }
);

export const removeUnit = createAsyncThunk(
  'curriculum/removeUnit',
  async (id: string) => {
    await CurriculumService.delete(id);
    return id;
  }
);

const curriculumSlice = createSlice({
  name: 'curriculum',
  initialState,
  reducers: {
    setUnits: (state, action: PayloadAction<CurriculumUnit[]>) => {
      state.units = action.payload;
    },
    clearUnits: (state) => {
      state.units = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addUnit.pending, (state) => {
        state.loading = true;
        delete state.error;
      })
      .addCase(addUnit.fulfilled, (state, action) => {
        state.loading = false;
        state.units.push(action.payload);
      })
      .addCase(addUnit.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to add unit';
      })
      .addCase(editUnit.pending, (state) => {
        state.loading = true;
        delete state.error;
      })
      .addCase(editUnit.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.units.findIndex(({id}) => id === action.payload.id);
        if (index !== -1) {
          state.units[index] = action.payload;
        }
      })
      .addCase(editUnit.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to edit unit';
      })
      .addCase(removeUnit.pending, (state) => {
        state.loading = true;
        delete state.error;
      })
      .addCase(removeUnit.fulfilled, (state, action) => {
        state.loading = false;
        state.units = state.units.filter((unit) => unit.id !== action.payload);
      })
      .addCase(removeUnit.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to remove unit';
      });
  },
});

export const {
  setUnits,
  clearUnits,
} = curriculumSlice.actions;

export default curriculumSlice.reducer;
